import { useMutation, UseMutationResult } from 'react-query';

import { useAuth } from 'src/hooks';

import { changeUserPassword } from 'src/utils/urls';

type ApiErrorItem = {
  Code: string;
  FieldName: string;
  Message: string;
};

type ApiResponse<TData = unknown> = {
  Data?: TData;
  Errors?: ApiErrorItem[];
  ErrorId?: string;
  IsSuccess: boolean;
  StatusCode: number;
};

type TResult = ApiResponse | 'Success';

type TVariables = {
  UserId: string;
  Password: string;
};

type TError = Error;

type TSnapshot = unknown;

async function changePassword({
  paramsData,
  token,
  tokenType,
}: {
  paramsData: TVariables;
  token: string;
  tokenType: string;
}): Promise<TResult> {
  const response = await fetch(changeUserPassword, {
    method: 'POST',
    headers: {
      Authorization: `${tokenType} ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      UserId: paramsData.UserId,
      NewPassword: paramsData.Password,
    }),
  });

  if (response.status === 200) {
    return 'Success';
  }

  let result: ApiResponse | null = null;

  try {
    const text = await response.text();

    result = text ? JSON.parse(text) : null;
  } catch (e) {
    console.error('JSON parse failed:', e);
  }

  if (!result) {
    throw new Error('Invalid server response');
  }

  return result;
}

function useChangePassword(): UseMutationResult<
  TResult,
  TError,
  TVariables,
  TSnapshot
> {
  const { token, tokenType } = useAuth();

  return useMutation(async (params: TVariables) => {
    const result = await changePassword({
      paramsData: params,
      token,
      tokenType,
    });

    if (result === 'Success') {
      return result;
    }

    if (!result.IsSuccess) {
      const errorMessage =
        result.Errors?.map(err => err.Message).join(', ') ||
        'Failed to change password';

      throw new Error(errorMessage);
    }

    return result;
  });
}

export { useChangePassword };
